import { useEffect, useState } from 'react';
import { listStudents } from '@dataconnect/generated';

const AdminReports = () => {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError('');
      try {
        const result = await listStudents();
        const students = result.data?.students || [];

        const countsMap = new Map();

        students.forEach((student) => {
          const schoolName = student.school?.name || 'No School';
          const grade = student.gradeLevel ?? 'N/A';
          const key = `${schoolName}__${grade}`;

          if (!countsMap.has(key)) {
            countsMap.set(key, {
              schoolName,
              grade,
              count: 0,
            });
          }

          countsMap.get(key).count += 1;
        });

        const sortedRows = Array.from(countsMap.values()).sort((a, b) => {
          if (a.schoolName === b.schoolName) {
            return (a.grade === 'N/A' ? 0 : a.grade) - (b.grade === 'N/A' ? 0 : b.grade);
          }
          return a.schoolName.localeCompare(b.schoolName);
        });

        setRows(sortedRows);
      } catch (err) {
        setError('Failed to load reports. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Reports</h2>
        <p className="text-sm text-gray-600">
          Overview of total student registrations grouped by school and grade.
        </p>
      </div>

      {isLoading && (
        <div className="text-sm text-gray-600">Loading report...</div>
      )}

      {error && (
        <div className="text-sm text-red-600">{error}</div>
      )}

      {!isLoading && !error && rows.length === 0 && (
        <div className="text-sm text-gray-600">No student registrations found.</div>
      )}

      {!isLoading && !error && rows.length > 0 && (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    School
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Grade
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student Count
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rows.map((row) => (
                  <tr key={`${row.schoolName}-${row.grade}`}>
                    <td className="px-4 py-2 text-sm text-gray-900">{row.schoolName}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{row.grade}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pt-3 text-sm text-gray-700 font-medium">
            Total students:&nbsp;
            {rows.reduce((sum, row) => sum + row.count, 0)}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminReports;
